import cx from "classnames";
import { Command as CMDK, defaultFilter, useCommandState } from "cmdk";
import { CaretRight } from "components/icons/CaretRight";
import { Search } from "components/icons/Search";
import {
  ComponentPropsWithoutRef,
  ComponentRef,
  forwardRef,
  HTMLAttributes,
  ReactNode,
} from "react";
import { Link } from "react-router-dom";

type ItemContentProps = {
  icon?: ReactNode;
  suffix?: ReactNode;
  children?: ReactNode;
};

type ItemProps = ComponentPropsWithoutRef<typeof CMDK.Item> &
  Omit<ItemContentProps, "children">;

type LinkItemProps = Omit<ItemProps, "onSelect"> & {
  to: string;
  onNavigate: (to: string) => void;
  onLinkClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
};

type ShortcutProps = {
  keys: string[];
  children: ReactNode;
};

const ROW_CLASS_NAME =
  "t-flex t-items-center t-gap-3 t-px-3 t-py-2 t-text-body t-text-text-100 t-no-underline";

const SELECTABLE_CLASS_NAME =
  "t-group t-cursor-pointer t-rounded-md data-[selected=true]:t-bg-neutral-0 data-[disabled=true]:t-cursor-not-allowed data-[disabled=true]:t-text-neutral-20";

const Kbd = ({ className, ...props }: HTMLAttributes<HTMLElement>) => (
  <kbd
    className={cx(
      "t-inline-flex t-h-5 t-min-w-5 t-items-center t-justify-center t-rounded t-border t-border-solid t-border-neutral-10 t-bg-surface t-px-1 t-font-sans t-text-body-sm t-text-text-60",
      className
    )}
    {...props}
  />
);

const ItemContent = ({ icon, suffix, children }: ItemContentProps) => (
  <>
    {icon && (
      <span className="t-flex t-size-5 t-shrink-0 t-items-center t-justify-center t-text-text-30">
        {icon}
      </span>
    )}
    <span className="t-min-w-0 t-flex-1 t-truncate t-text-body-sm">
      {children}
    </span>
    {suffix}
    <Kbd
      aria-hidden
      className="t-hidden group-data-[selected=true]:t-inline-flex"
    >
      Enter
    </Kbd>
    <CaretRight
      aria-hidden
      strokeWidth="1.2"
      className="t-shrink-0 t-text-text-30 group-data-[selected=true]:t-text-purple-40"
    />
  </>
);

const Root = forwardRef<
  ComponentRef<typeof CMDK>,
  ComponentPropsWithoutRef<typeof CMDK>
>(({ className, ...props }, ref) => (
  <CMDK
    ref={ref}
    className={cx("t-flex t-min-h-0 t-flex-col", className)}
    {...props}
  />
));

const Input = forwardRef<
  ComponentRef<typeof CMDK.Input>,
  ComponentPropsWithoutRef<typeof CMDK.Input>
>((props, ref) => <CMDK.Input ref={ref} {...props} />);

const SearchInput = forwardRef<
  ComponentRef<typeof CMDK.Input>,
  ComponentPropsWithoutRef<typeof CMDK.Input>
>(({ className, ...props }, ref) => (
  <div className="t-flex t-shrink-0 t-items-center t-gap-3 t-border-0 t-border-b t-border-solid t-border-neutral-0 t-px-4 t-py-4">
    <Search />
    <CMDK.Input
      ref={ref}
      className={cx(
        "t-w-full t-border-none t-bg-transparent t-p-0 t-text-body t-text-text-100 placeholder:t-text-neutral-40 focus:t-outline-none focus:t-ring-0",
        className
      )}
      {...props}
    />
  </div>
));

const List = forwardRef<
  ComponentRef<typeof CMDK.List>,
  ComponentPropsWithoutRef<typeof CMDK.List>
>(({ className, ...props }, ref) => (
  <CMDK.List
    ref={ref}
    className={cx("t-overflow-y-auto t-p-2", className)}
    {...props}
  />
));

const Empty = forwardRef<
  ComponentRef<typeof CMDK.Empty>,
  ComponentPropsWithoutRef<typeof CMDK.Empty>
>(({ className, ...props }, ref) => (
  <CMDK.Empty
    ref={ref}
    className={cx(
      "t-p-4 t-text-center t-text-body-sm t-text-text-30 t-h-96 t-justify-center t-flex t-items-center",
      className
    )}
    {...props}
  />
));

const Loading = forwardRef<
  ComponentRef<typeof CMDK.Loading>,
  ComponentPropsWithoutRef<typeof CMDK.Loading>
>(({ className, ...props }, ref) => (
  <CMDK.Loading
    ref={ref}
    className={cx(
      "t-flex t-justify-center t-p-4 t-text-body-sm t-text-text-30",
      className
    )}
    {...props}
  />
));

const Group = forwardRef<
  ComponentRef<typeof CMDK.Group>,
  ComponentPropsWithoutRef<typeof CMDK.Group>
>(({ className, ...props }, ref) => (
  <CMDK.Group
    ref={ref}
    className={cx(
      "[&_[cmdk-group-heading]]:t-px-3 [&_[cmdk-group-heading]]:t-pt-2 [&_[cmdk-group-heading]]:t-pb-1 [&_[cmdk-group-heading]]:t-text-subtext-sm [&_[cmdk-group-heading]]:t-uppercase [&_[cmdk-group-heading]]:t-tracking-wide [&_[cmdk-group-heading]]:t-text-text-30",
      className
    )}
    {...props}
  />
));

const Separator = forwardRef<
  ComponentRef<typeof CMDK.Separator>,
  ComponentPropsWithoutRef<typeof CMDK.Separator>
>(({ className, ...props }, ref) => (
  <CMDK.Separator
    ref={ref}
    className={cx("t-my-2 t-h-px t-bg-neutral-10", className)}
    {...props}
  />
));

const Item = forwardRef<ComponentRef<typeof CMDK.Item>, ItemProps>(
  ({ icon, suffix, children, className, ...props }, ref) => (
    <CMDK.Item
      ref={ref}
      className={cx(SELECTABLE_CLASS_NAME, ROW_CLASS_NAME, className)}
      {...props}
    >
      <ItemContent icon={icon} suffix={suffix}>
        {children}
      </ItemContent>
    </CMDK.Item>
  )
);

const LinkItem = forwardRef<ComponentRef<typeof CMDK.Item>, LinkItemProps>(
  (
    {
      to,
      onNavigate,
      onLinkClick,
      icon,
      suffix,
      children,
      className,
      ...props
    },
    ref
  ) => {
    const handleSelect = () => onNavigate(to);

    return (
      <CMDK.Item
        ref={ref}
        onSelect={handleSelect}
        className={cx(SELECTABLE_CLASS_NAME, className)}
        {...props}
      >
        <Link to={to} onClick={onLinkClick} className={ROW_CLASS_NAME}>
          <ItemContent icon={icon} suffix={suffix}>
            {children}
          </ItemContent>
        </Link>
      </CMDK.Item>
    );
  }
);

const Footer = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cx(
      "t-flex t-shrink-0 t-flex-wrap t-items-center t-gap-4 t-border-0 t-border-t t-border-solid t-border-neutral-0 t-px-4 t-py-2 t-text-body-sm t-text-text-60",
      className
    )}
    {...props}
  />
);

const Shortcut = ({ keys, children }: ShortcutProps) => (
  <span className="t-flex t-items-center t-gap-1.5">
    <span className="t-flex t-items-center t-gap-1">
      {keys.map((key) => (
        <Kbd key={key}>{key}</Kbd>
      ))}
    </span>
    {children}
  </span>
);

const Command = {
  Root,
  Input,
  SearchInput,
  List,
  Empty,
  Loading,
  Group,
  Separator,
  Item,
  LinkItem,
  Kbd,
  Footer,
  Shortcut,
  Dialog: CMDK.Dialog,
  useCommandState,
  defaultFilter,
};

export default Command;
